import React, { useState } from "react";
import Nav from "./Navigation.js";

const LightsOutSolver = ({ isModal = false, onClose }) => {
  const [input, setInput] = useState(Array(9).fill(false));
  const [result, setResult] = useState(Array(9).fill(false));
  const [hasSolved, setHasSolved] = useState(false);
  const [noSolution, setNoSolution] = useState(false);

  const press = (input, pressed) => {
    if (pressed & 1) input = input ^ 0xb;
    if (pressed & 2) input = input ^ 0x17;
    if (pressed & 4) input = input ^ 0x26;
    if (pressed & 8) input = input ^ 0x59;
    if (pressed & 16) input = input ^ 0xba;
    if (pressed & 32) input = input ^ 0x134;
    if (pressed & 64) input = input ^ 0xc8;
    if (pressed & 128) input = input ^ 0x1d0;
    if (pressed & 256) input = input ^ 0x1a0;
    return input;
  };

  const solve = () => {
    let grid = 0;
    for (let i = 0; i < 9; ++i) {
      grid = (grid << 1) + (input[i] ? 1 : 0);
    }

    let sol;
    for (sol = 0; sol < 512; ++sol) {
      if (press(grid, sol) === 511) break;
    }

    if (sol === 512) {
      setNoSolution(true);
      setHasSolved(false);
      return;
    }

    const newResult = [];
    for (let i = 8; i >= 0; --i, sol >>= 1) {
      newResult[i] = sol & 1;
    }

    setResult(newResult);
    setHasSolved(true);
    setNoSolution(false);
  };

  const handleInputChange = (index) => {
    const newInput = [...input];
    newInput[index] = !newInput[index];
    setInput(newInput);
    setHasSolved(false);
    setNoSolution(false);
  };

  const reset = () => {
    setInput(Array(9).fill(false));
    setResult(Array(9).fill(false));
    setHasSolved(false);
    setNoSolution(false);
  };

  const renderGrid = (values, onClick, label, sublabel) => (
    <div className="mb-4">
      <p className="font-semibold text-white mb-1">{label}</p>
      {sublabel && <p className="text-gray-400 text-sm mb-2">{sublabel}</p>}
      <table className="mx-auto">
        <tbody>
          {[0, 1, 2].map((rowIndex) => (
            <tr key={rowIndex}>
              {[0, 1, 2].map((colIndex) => {
                const index = rowIndex * 3 + colIndex;
                const isOn = values[index];
                let cellClass;
                if (onClick) {
                  cellClass = isOn
                    ? "bg-yellow-400 border-yellow-300 shadow-md"
                    : "bg-gray-700 border-gray-600 hover:bg-gray-600";
                } else if (hasSolved) {
                  cellClass = isOn
                    ? "bg-green-400 border-green-300 shadow-md"
                    : "bg-gray-700 border-gray-600";
                } else {
                  cellClass = "bg-gray-700 border-gray-600 opacity-40";
                }
                return (
                  <td key={index} className="p-1">
                    <div
                      className={`w-16 h-16 rounded-md border flex items-center justify-center transition-colors duration-150 ${onClick ? "cursor-pointer" : ""} ${cellClass}`}
                      onClick={onClick ? () => onClick(index) : undefined}
                    >
                      {!!isOn && (
                        <span className="text-lg select-none">
                          {onClick ? "●" : "✓"}
                        </span>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="text-white">
      {!isModal && <Nav />}
      <div className="container mx-auto my-8 text-center">
        <div className="flex items-center justify-between mb-2 px-2">
          <h1 className="text-3xl font-bold flex-1">Light Puzzle Solver</h1>
          {isModal && onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl leading-none ml-2" aria-label="Close">✕</button>
          )}
        </div>
        <p className="text-gray-400 mb-6">
          Stand at one end of the puzzle. Enter the lit tiles you see, then hit{" "}
          <span className="text-white font-medium">Solve Puzzle</span> to see which tiles to step on.
        </p>

          <div className="inline-block bg-gray-800 rounded-xl p-6 shadow-lg">
            {renderGrid(
              input,
              handleInputChange,
              "Step 1 — Enter your board pattern",
              "Click each tile that is currently lit"
            )}

            <div className="mb-6 flex flex-wrap justify-center gap-3">
              <button
                className="bg-blue-500 text-white py-2 px-5 rounded hover:bg-blue-600 focus:outline-none transition-colors"
                onClick={solve}
              >
                Solve Puzzle
              </button>
              <button
                className="bg-red-500 text-white py-2 px-5 rounded hover:bg-red-600 focus:outline-none transition-colors"
                onClick={reset}
              >
                Reset
              </button>
            </div>

            {noSolution && (
              <p className="text-red-400 font-semibold mb-4">
                No solution found — double-check the pattern you entered.
              </p>
            )}

            {renderGrid(
              result,
              null,
              "Step 2 — Press these tiles",
              hasSolved
                ? "Green tiles = step on these to solve the puzzle"
                : "Solution will appear here after you click Solve"
            )}
          </div>
        </div>
    </div>
  );
};

export default LightsOutSolver;
